import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import type { BaseProperty } from '@app/shared/models/common-models'
import { LocalizeNumberPropertyPipe } from '@app/shared/pipes/localize-number-property/localize-number-property.pipe'
import { LocalizeNumberPipe } from '@app/shared/pipes/localize-number/localize-number.pipe'

/**
 * Service for entity property -related utilities
 */
@Injectable()
export class PropertyService {
  private readonly numberTag = '{number:'

  private readonly paramSeparator = ';'

  constructor(
    private translateService: TranslateService,
    private localizeNumberPropertyPipe: LocalizeNumberPropertyPipe,
    private localizeNumberPipe: LocalizeNumberPipe,
  ) {}

  /**
   * Returns a formatted version of the value where any interpolation tags '{tag}' are handled.
   * @param property The property to use. Parts of the property such as measurement unit might be used.
   */
  injectTranslatedValuesToPropertyValue(property: BaseProperty): string {
    if (typeof property?.Value !== 'string') {
      return String(property?.Value ?? '')
    }

    const pattern = /{(.*?)}/gm
    const matches = property.Value.match(pattern)

    // If there are no matches, return the value as-is
    if (!matches) {
      return property.Value
    }

    let value = property.Value

    for (let i = 0; i < matches.length; i++) {
      const match = matches[i]

      // If the matching part is '{unit}', then we'll replace it with the current measurement unit translation.
      // In any other case, we simply use the contents of the {} as a translation key and replace the tag with the translation result.
      if (match.includes('{unit}')) {
        value = value.replace(match, this.translateService.instant(property.MeasurementUnitTextId))
      } else if (match.startsWith(this.numberTag)) {
        value = value.replace(match, this.handleNumberValueToken(match, property))
      } else {
        value = value.replace(match, this.translateService.instant(match.replace('{', '').replace('}', '')))
      }
    }

    return value
  }

  /**
   * Handles a number interpolation token by extracting the number from the string and using the number localization pipe
   * to format it into a localized string.
   * @param token The number token, such as "{number:1234.56}"
   * @param property The property which's formatting rules should be applied
   */
  private handleNumberValueToken(token: string, property: BaseProperty): string {
    // Remove the token parts of the string and then split it using the parameter separator to get the number value and the optional
    // decimal precision. If decimal precision is set, we'll parse and use that. Otherwise, we use the decimal count set in the
    // property data.
    const [numberStr, decimalsStr] = token.replace(this.numberTag, '').replace('}', '').split(this.paramSeparator)

    const numericValue = parseFloat(numberStr)

    // No explicit decimal count -> use property format
    if (decimalsStr === undefined) {
      return this.localizeNumberPropertyPipe.transform(property, numericValue)
    }

    // Now we know there is a valid decimal count, so we'll parse it and use it for the formatting

    const decimalCount = parseInt(decimalsStr, 10)

    const intlOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: decimalCount,
      maximumFractionDigits: decimalCount,
    }

    return this.localizeNumberPipe.transform(numericValue, intlOptions)
  }
}
